import FastPoissonDiskSampling from "fast-2d-poisson-disk-sampling";

export function createTempCanvas(
  width: number,
  height: number,
  offscreen = true,
): OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D {
  let tempCanvas: OffscreenCanvas | HTMLCanvasElement;

  if (offscreen && typeof OffscreenCanvas !== "undefined") {
    // Use OffscreenCanvas if supported
    tempCanvas = new OffscreenCanvas(width, height);
  } else {
    // Fallback to regular canvas
    tempCanvas = document.createElement("canvas");
    tempCanvas.width = width;
    tempCanvas.height = height;
  }

  return tempCanvas.getContext("2d", { willReadFrequently: true }) as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D;
}

export function clearImageBackground(
  imageSource,
  minBrightness = 0,
  maxBrightness = 250,
  contrastFactor = 1.5,
  exposureFactor = 0.5,
  saturationFactor = 3.0,
) {
  const img = new Image();
  img.src = imageSource;
  const canvas = document.createElement("canvas");

  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    const context = canvas.getContext("2d");
    context.drawImage(img, 0, 0);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    context.filter = `contrast(${contrastFactor})`;

    for (let i = 0; i < canvas.width; i += 1) {
      for (let j = 0; j < canvas.height; j += 1) {
        const index = (j * canvas.width + i) * 4;
        const r = pixels[index];
        const g = pixels[index + 1];
        const b = pixels[index + 2];
        // const a = pixels[index + 3];

        const brightness = (r + g + b) / 3;

        let transparency =
          255 - ((brightness - minBrightness) * 255) / (maxBrightness - minBrightness);
        transparency = Math.max(0, Math.min(transparency, 255));

        if (brightness === 255 || transparency === 0) {
          transparency = 0;
          pixels[index] = 255;
          pixels[index + 1] = 255;
          pixels[index + 2] = 255;
        }

        pixels[index + 3] = transparency;
      }
    }

    context.filter = `brightness(${exposureFactor}) saturate(${saturationFactor}) contrast(${contrastFactor})`;

    context.putImageData(imageData, 0, 0);
  };

  return canvas;
}

// Generate a Dense Point Set
const generateDensePoints = (width: number, height: number, minDistance: number) => {
  const poissonDisk = new FastPoissonDiskSampling({
    shape: [width, height],
    minDistance: minDistance,
    tries: 30,
  });
  const points = poissonDisk.fill();
  // Round to nearest integer
  return points.map(([x, y]) => [Math.round(x), Math.round(y)]);
};

const sigmoid = (x) => 1 / (1 + Math.exp(-x));
// Prune Points Based on Pixel Blackness
const prunePointsBasedOnPixels = (
  points: number[][],
  pixels: Uint8ClampedArray,
  width: number,
) => {
  const prunedPoints = points.filter(([x, y]) => {
    const index = (y * width + x) * 4;
    const grayscale = pixels[index];
    if (grayscale > 240) {
      return false;
    }
    // Apply the sigmoid transformation to grayscale
    const normalizedGrayscale = 1 - grayscale / 255;
    const probability = sigmoid((normalizedGrayscale - 0.25) * 25); // Adjust factor (25) for subtle or sharp effect
    return Math.random() < probability;
  });
  return prunedPoints;
};

export function grayscalePixels(
  context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
): Uint8ClampedArray {
  const pixels = context.getImageData(
    0,
    0,
    context.canvas.width,
    context.canvas.height,
  ).data;

  let darkestPixel = 255;
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];

    const avg = (r + g + b) / 3;

    pixels[i] = avg;
    pixels[i + 1] = avg;
    pixels[i + 2] = avg;

    if (avg < darkestPixel) {
      darkestPixel = avg;
    }
  }

  // Normalize so that darkest pixel is 0 and brightest pixel is 255
  for (let i = 0; i < pixels.length; i += 4) {
    const avg = pixels[i];
    pixels[i] = (avg - darkestPixel) * (255 / (255 - darkestPixel));
    pixels[i + 1] = pixels[i];
    pixels[i + 2] = pixels[i];
  }

  return pixels;
}

// Top level function to apply stippling effect
export function stipplingEffect(
  context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  minDistance: number,
  threshold: number,
  pointRadius: number[] = [1.5, 2.5],
) {
  const { width, height } = context.canvas;

  const pixels = grayscalePixels(context);

  const { whiteThresh, blackThresh } = calcThresholds(context, threshold);

  // Generate dense points
  const densePoints = generateDensePoints(width, height, minDistance);

  // Prune points based on image blackness
  const prunedPoints = prunePointsBasedOnPixels(densePoints, pixels, width);

  // Draw pruned points
  context.fillStyle = "black";
  prunedPoints.forEach(([x, y]) => {
    // Random integer radius plus/minus 1 from pointRadius
    const randomSize = pointRadius[Math.floor(Math.random() * pointRadius.length)];
    context.beginPath();
    context.arc(x, y, randomSize, 0, Math.PI * 2);
    context.fill();
  });

  // threshold should be doubled
  blackWhiteEffect(context, whiteThresh, blackThresh);

  return context;
}

export function calcThresholds(
  canvas: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  threshold: number = 0.25,
  thresholdBand: number = 0.2,
) {
  const rgba = canvas.getImageData(
    0,
    0,
    canvas.canvas.width,
    canvas.canvas.height,
  ).data;

  let totalGrayscale = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    const grayscale = rgba[i]; // Use the red channel for simplicity
    totalGrayscale += grayscale;
  }

  const avgGrayscale = totalGrayscale / ((255 * rgba.length) / 4);

  const whiteThresh =
    (avgGrayscale > 0.85 ? 0.5 : 1) * (threshold + thresholdBand) * 255;
  const blackThresh = (threshold - thresholdBand) * 255;

  return { whiteThresh, blackThresh };
}

export function blackWhiteEffect(
  context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  whiteThreshold: number,
  blackThreshold: number,
) {
  const imageData = context.getImageData(
    0,
    0,
    context.canvas.width,
    context.canvas.height,
  );
  const rgba = imageData.data;

  for (let i = 0; i < rgba.length; i += 4) {
    const grayscale = rgba[i];
    const invertedGrayscale = 255 - grayscale;

    if (invertedGrayscale > whiteThreshold || invertedGrayscale < blackThreshold) {
      const color = invertedGrayscale > whiteThreshold ? 0 : 255;
      rgba[i] = rgba[i + 1] = rgba[i + 2] = color; // Set R, G, B to color
    } else {
      rgba[i] = rgba[i + 1] = rgba[i + 2] = 255; // Set R, G, B to white
    }
  }

  context.putImageData(imageData, 0, 0);
}

export function convertWhiteToTransparent(
  context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  minBrightness: number,
  maxBrightness: number,
) {
  const imageData = context.getImageData(
    0,
    0,
    context.canvas.width,
    context.canvas.height,
  );
  const pixels = imageData.data;

  for (let i = 0; i < context.canvas.width; i += 1) {
    for (let j = 0; j < context.canvas.height; j += 1) {
      const index = (j * context.canvas.width + i) * 4;
      const r = pixels[index];
      const g = pixels[index + 1];
      const b = pixels[index + 2];
      // const a = pixels[index + 3];

      const brightness = (r + g + b) / 3;

      let transparency =
        255 - ((brightness - minBrightness) * 255) / (maxBrightness - minBrightness);
      transparency = Math.max(0, Math.min(transparency, 255));

      if (brightness === 255 || transparency === 0) {
        transparency = 0;
        pixels[index] = 255;
        pixels[index + 1] = 255;
        pixels[index + 2] = 255;
      }

      pixels[index + 3] = transparency;
    }
  }
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.putImageData(imageData, 0, 0);

  return context;
}

export function adjustFilters(
  context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  contrastFactor: number,
  exposureFactor: number,
  saturationFactor: number,
  blurRadius: number,
  opacityFactor: number,
) {
  context.filter = `contrast(${contrastFactor}) brightness(${exposureFactor}) saturate(${saturationFactor}) blur(${blurRadius}px) opacity(${opacityFactor})`;
  return context;
}
